import { useCallback, useEffect, useState } from "react";
import { spaceActions } from "redux/Ldb/actions/spaceActions";
import { useAppDispatch, useAppSelector } from "../storeHooks";
import { SpaceMessage as SpaceMessageType } from "types/spaceTypes";

const usePinSpaceMessage = (): usePinSpaceMessageProp => {
    const dispatch = useAppDispatch();
    const [pinnedMessages, setPinnedMessages] = useState<SpaceMessageType[]>([]);

    const {
        pinningSpaceMessage,
        pinnedSpaceMessageSuccess,
        pinnedSpaceMessageFailure,
        unpinnedSpaceMessageSuccess,
    } = useAppSelector((state) => state.space);

    async function pinSpaceMessage(spaceId: string, messageId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            // Reset any previous state
            dispatch(spaceActions.resetPinSpaceMessage());

            // Start the Redux action
            dispatch(spaceActions.pinSpaceMessage(spaceId, messageId));

            // Poll for state changes
            const checkState = () => {
                const currentState = dispatch((dispatch, getState) => {
                    const state = getState();
                    return {
                        isLoading: state.space.pinningSpaceMessage,
                        success: state.space.pinnedSpaceMessageSuccess,
                        error: state.space.pinnedSpaceMessageFailure,
                    };
                });

                if (currentState.error) {
                    reject(currentState.error);
                } else if (currentState.success && !currentState.isLoading) {
                    resolve(currentState.success);
                } else if (
                    !currentState.isLoading &&
                    !currentState.success &&
                    !currentState.error
                ) {
                    reject("Request failed without error or success response");
                } else {
                    // Still loading, check again
                    setTimeout(checkState, 100);
                }
            };

            checkState();
        });
    }

    const resetPinSpaceMessage = useCallback(() => {
        dispatch(spaceActions.resetPinSpaceMessage());
    }, [dispatch]);

    // TODO: This is a temporary solution to update the pinned messages when a message is unpinned
    useEffect(() => {
        if (Boolean(unpinnedSpaceMessageSuccess)) {
            setPinnedMessages((prev) =>
                prev.filter((message) => message.id !== unpinnedSpaceMessageSuccess.data.messageId)
            );
        }
    }, [unpinnedSpaceMessageSuccess]);

    return [
        pinSpaceMessage,
        {
            isLoading: pinningSpaceMessage,
            isSuccess: Boolean(pinnedSpaceMessageSuccess),
            isError: Boolean(pinnedSpaceMessageFailure),
            data: pinnedSpaceMessageSuccess,
            error: pinnedSpaceMessageFailure,
            resetPinSpaceMessage,
        },
    ];
};

type usePinSpaceMessageProp = [
    (spaceId: string, messageId: string) => Promise<any>,
    {
        isLoading: boolean;
        isSuccess: boolean;
        isError: boolean;
        data: any;
        error: any;
        resetPinSpaceMessage: () => void;
    }
];

export default usePinSpaceMessage;
